import { createContext, useCallback, useContext, useRef, useState } from 'react';

import { WebSocketContext } from '../../websocket-context/websocket-provider';
import {
  IPortfolioPerformanceAccounts,
  IPortfolioPerformanceAccountMetricTimeseriesData,
  IPortfolioPerformanceProvider,
} from '../portfolio-performance.types';
import { MetricKey } from './risk.types';
import { computeTimeRange, sumAlignedSeries } from './risk.helpers';

export const PortfolioPerformanceContext = createContext({} as IPortfolioPerformanceProvider);

// A fetch targets one or more settlement-currency books. Single-symbol callers
// pass `token` (unchanged wire behavior); the combined-USD view passes `tokens`
// with the constituent symbols to sum element-wise by timestamp.
type FetchParams = { account: IPortfolioPerformanceAccounts } & (
  { token: string } | { tokens: string[] }
) &
  (
    | { range: string; riskTokenType?: string }
    | { begin: string; end: string; frequency: number; append?: boolean; riskTokenType?: string }
  );

const tokensOf = (params: FetchParams): string[] =>
  'tokens' in params ? params.tokens : [params.token];

export const useMetricTimeseries = (metric: MetricKey) => {
  const { sendMessage, subscribeToChannel } = useContext(WebSocketContext);
  const [data, setData] = useState<IPortfolioPerformanceAccountMetricTimeseriesData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Raw per-symbol series for the active selection, kept so each incoming book
  // response can be re-summed against the others. `data` above always holds the
  // element-wise sum (which for the single-symbol case is just that one book).
  const rawBySymbolRef = useRef<Record<string, [number, number][]>>({});
  const activeSymbolsRef = useRef<string[]>([]);
  // Bumped on every fresh (non-append) fetch so late responses from a
  // superseded selection are ignored instead of corrupting the current sum.
  const genRef = useRef(0);
  const pendingRef = useRef(0);

  const refetch = useCallback(
    (params: FetchParams) => {
      const symbols = tokensOf(params).map((t) => t.replace(/ /g, ''));
      const isAppend = 'append' in params && !!params.append;

      let begin: string, end: string, frequency: number;
      if ('begin' in params && 'end' in params && 'frequency' in params) {
        begin = params.begin;
        end = params.end;
        frequency = params.frequency;
      } else {
        const built = computeTimeRange((params as { range: string }).range);
        begin = built.begin;
        end = built.end;
        frequency = built.frequency;
      }

      const gen = isAppend ? genRef.current : (genRef.current += 1);

      if (!isAppend) {
        setError(false);
        setLoading(true);
        setData(null);
        rawBySymbolRef.current = {};
        activeSymbolsRef.current = symbols;
        pendingRef.current = symbols.length;
      }

      const riskTokenType =
        'riskTokenType' in params && params.riskTokenType ? params.riskTokenType : 'QUOTE';

      symbols.forEach((symbol) => {
        // Single-symbol fetches keep the historical (symbol-less) channel id so
        // their wire behavior is byte-for-byte unchanged; combined fetches add
        // the symbol so each book's response lands on its own handler slot
        // (mirrors the per-account scoping already in place).
        const channelId =
          symbols.length > 1
            ? `PP_METRIC_TS_${params.account.name}_${metric}_${symbol}`
            : `PP_METRIC_TS_${params.account.name}_${metric}`;

        const payload = {
          jsonrpc: '2.0',
          id: channelId,
          method: 'portfolio_performance.account_metric_timeseries',
          params: {
            account: params.account.name,
            type: riskTokenType,
            symbol,
            metric,
            begin,
            end,
            frequency,
          },
        };

        sendMessage(JSON.stringify(payload));
        subscribeToChannel(channelId, (response: any) => {
          if (gen !== genRef.current) return; // stale selection
          if (!response) return;

          if (response.error) {
            setError(true);
            if (!isAppend) setLoading(false);
            return;
          }
          if (!response.result) return;

          const incoming: [number, number][] = response.result.data ?? [];
          const existing = rawBySymbolRef.current[symbol] ?? [];
          if (isAppend) {
            const existingStart = existing[0]?.[0] ?? Infinity;
            const newPoints = incoming.filter(([ts]) => ts < existingStart);
            rawBySymbolRef.current[symbol] = [...newPoints, ...existing];
          } else {
            rawBySymbolRef.current[symbol] = incoming;
            pendingRef.current -= 1;
            if (pendingRef.current <= 0) setLoading(false);
          }

          const summed = sumAlignedSeries(
            activeSymbolsRef.current.map((s) => rawBySymbolRef.current[s]),
          );
          setData({ ...response.result, data: summed });
        });
      });
    },
    [metric, sendMessage],
  );

  return {
    data,
    loading,
    error,
    refetch,
  };
};
